import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { ArrowLeft, Lock, Minus, Plus, ShoppingCart, PackageCheck, Warehouse, Tag, Download } from "lucide-react";
import { toast } from "sonner";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import BuyerApprovalBanner from "@/components/BuyerApprovalBanner";
import QuoteRequestModal from "@/components/QuoteRequestModal";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { useAuth } from "@/context/AuthContext";
import { useCart } from "@/context/CartContext";
import api from "@/lib/api";
import { formatINR, tierRate } from "@/lib/format";
import ProductImg from "@/components/ProductImg";
import { SizeChips, cartLine } from "@/components/ProductCard";
import { ChevronLeft, ChevronRight } from "lucide-react";

export default function ProductDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, approved } = useAuth();
  const { addItem } = useCart();
  const [product, setProduct] = useState(null);
  const [qty, setQty] = useState(1);
  const [variantId, setVariantId] = useState(null);
  const [imgIdx, setImgIdx] = useState(0);

  useEffect(() => {
    api.get(`/products/${id}`).then((r) => {
      const d = r.data;
      const vs = d.variants || [];
      const sel = vs.find((v) => v.id === d.selected_variant_id && v.in_stock) || vs.find((v) => v.in_stock) || vs[0];
      setProduct(d);
      setVariantId(sel?.id || null);
      setQty(sel?.moq || d.moq || 1);
      setImgIdx(0);
    });
  }, [id]);

  if (!product) return <div className="min-h-screen bg-slate-50"><Navbar /></div>;

  const variants = product.variants || [];
  const current = variants.find((v) => v.id === variantId) || variants[0] || product;
  const hasVariants = variants.length > 1;
  const pack = current.pack_size || 1;
  const unitRate = approved ? tierRate({ rate: current.rate, tier_prices: current.tier_prices || [] }, qty) : null;

  const images = product.images || [];
  const hero = images[imgIdx] || { url: product.image_large || product.image_url, thumb: product.image_url };
  const step = (d) => setImgIdx((i) => (i + d + images.length) % images.length);

  const handleAdd = () => {
    if (qty < (current.moq || 1)) return toast.error(`Minimum order is ${current.moq}.`);
    if (qty > (current.stock_on_hand || 0)) return toast.error(`Only ${current.stock_on_hand} in stock.`);
    addItem(cartLine(product, current), qty);
    toast.success("Added to cart");
  };

  const downloadImages = async () => {
    try {
      const res = await api.get(`/products/${id}/images.zip`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/zip" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `${(product.sku || "product").replace(/\//g, "-")}_images.zip`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Images downloaded");
    } catch {
      toast.error("No images available to download");
    }
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <Navbar />
      <BuyerApprovalBanner />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <button onClick={() => navigate(-1)} className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-900 mb-6" data-testid="back-btn">
          <ArrowLeft className="h-4 w-4" /> Back to catalogue
        </button>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10">
          <div>
            <div className="relative rounded-3xl overflow-hidden bg-white border border-slate-200 aspect-square" data-testid="product-hero-image">
              <ProductImg src={hero.url} fallback={hero.thumb} alt={product.alt || product.name} eager className="h-full w-full object-contain p-4" testId="detail-main-img" />
              {images.length > 1 && (
                <>
                  <button onClick={() => step(-1)} aria-label="Previous image" data-testid="gallery-prev" className="absolute left-3 top-1/2 -translate-y-1/2 h-10 w-10 rounded-full bg-white/90 border border-slate-200 shadow flex items-center justify-center hover:bg-white"><ChevronLeft className="h-5 w-5" /></button>
                  <button onClick={() => step(1)} aria-label="Next image" data-testid="gallery-next" className="absolute right-3 top-1/2 -translate-y-1/2 h-10 w-10 rounded-full bg-white/90 border border-slate-200 shadow flex items-center justify-center hover:bg-white"><ChevronRight className="h-5 w-5" /></button>
                  <span className="absolute bottom-3 right-3 rounded-full bg-slate-900/80 text-white text-xs px-2.5 py-1 font-mono-num" data-testid="gallery-counter">{imgIdx + 1}/{images.length}</span>
                </>
              )}
            </div>
            {images.length > 1 && (
              <div className="mt-3 flex gap-2 overflow-x-auto pb-1" data-testid="product-gallery">
                {images.map((im, idx) => (
                  <button
                    key={im.url}
                    onClick={() => setImgIdx(idx)}
                    data-testid={`gallery-thumb-${idx}`}
                    className={`shrink-0 h-16 w-16 rounded-xl overflow-hidden border bg-white transition-colors ${idx === imgIdx ? "border-slate-900 ring-1 ring-slate-900" : "border-slate-200 hover:border-slate-400"}`}
                  >
                    <ProductImg src={im.thumb || im.url} fallback={im.url} alt={`${im.label || product.name} – photo ${idx + 1}`} className="h-full w-full object-contain p-1" />
                  </button>
                ))}
              </div>
            )}
            {approved && (
              <Button
                onClick={downloadImages}
                variant="outline"
                className="mt-4 w-full rounded-full h-11 border-slate-300 text-slate-800 font-semibold hover:border-slate-900 hover:bg-slate-50"
                data-testid="detail-download-images"
              >
                <Download className="h-4 w-4 mr-2" /> Download all images (ZIP)
              </Button>
            )}
          </div>

          <div>
            <Badge className="bg-[#FFDA00] text-slate-900 border-0 mb-3">{product.category}</Badge>
            <h1 className="font-heading text-3xl sm:text-4xl font-extrabold tracking-tight text-slate-900">
              {product.name}
            </h1>
            <div className="mt-2 text-sm font-mono-num text-slate-400" data-testid="detail-design-code">
              {product.brand && <span className="mr-3 font-sans font-semibold uppercase tracking-wider text-slate-500">{product.brand}</span>}
              {product.design_code ? `Design ${product.design_code}` : ""}{current.sku ? ` · SKU ${current.sku}` : ""}
            </div>
            <p className="mt-4 text-slate-600 leading-relaxed">{product.description}</p>

            <div className="mt-6 flex flex-wrap gap-4 text-sm">
              <div className="flex items-center gap-2 text-slate-600">
                <PackageCheck className="h-4 w-4 text-slate-400" /> MOQ {current.moq || 1} {current.unit || product.unit}
              </div>
              {pack > 1 && (
                <div className="flex items-center gap-2 text-slate-600">
                  <Tag className="h-4 w-4 text-slate-400" /> Packs of {pack}
                </div>
              )}
              {approved && (
                <div className="flex items-center gap-2 text-slate-600">
                  <Warehouse className="h-4 w-4 text-slate-400" />
                  {current.in_stock ? `${current.stock_on_hand} ${current.unit || product.unit} available` : "Out of stock"}
                </div>
              )}
            </div>

            {hasVariants && (
              <div className="mt-6" data-testid="variant-selector">
                <div className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-2">
                  Select size <span className="normal-case font-normal text-slate-400">· greyed sizes are sold out</span>
                </div>
                <SizeChips variants={variants} selectedId={current.id} max={40} testPrefix="detail"
                  onSelect={(vid) => { const v = variants.find((x) => x.id === vid); setVariantId(vid); setQty(v?.moq || 1); }} />
              </div>
            )}

            <div className="mt-8 rounded-2xl border border-slate-200 bg-white p-6">
              {!approved ? (
                <div className="text-center py-4">
                  <Lock className="h-8 w-8 mx-auto text-slate-300 mb-3" />
                  <div className="font-semibold text-slate-900">Wholesale pricing is account-gated</div>
                  <p className="text-sm text-slate-500 mt-1 mb-4">
                    {user ? "Your account is awaiting admin approval." : "Register your business to view live prices, stock and order."}
                  </p>
                  <Button onClick={() => navigate(user ? "/account" : "/register")} className="rounded-full bg-slate-900 hover:bg-black text-white" data-testid="detail-unlock-btn">
                    {user ? "Check account status" : "Register for wholesale access"}
                  </Button>
                </div>
              ) : (
                <>
                  <div className="flex items-end justify-between">
                    <div>
                      <div className="font-mono-num text-4xl font-bold text-slate-900">{formatINR(unitRate)}</div>
                      <div className="text-sm text-slate-400 mt-1">per {product.unit.replace(/s$/, "")} · excl. tax</div>
                    </div>
                    <div className="flex items-center rounded-full border border-slate-200">
                      <button className="p-3 text-slate-500 hover:text-slate-900" onClick={() => setQty((q) => Math.max(current.moq || 1, q - pack))} data-testid="detail-qty-dec">
                        <Minus className="h-4 w-4" />
                      </button>
                      <span className="w-12 text-center font-semibold font-mono-num" data-testid="detail-qty-value">{qty}</span>
                      <button className="p-3 text-slate-500 hover:text-slate-900" onClick={() => setQty((q) => q + pack)} data-testid="detail-qty-inc">
                        <Plus className="h-4 w-4" />
                      </button>
                    </div>
                  </div>

                  {current.tier_prices?.length > 0 && (
                    <div className="mt-5 rounded-xl bg-slate-50 p-4">
                      <div className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-2">Volume pricing</div>
                      <div className="grid grid-cols-2 gap-2 text-sm">
                        <div className="flex justify-between"><span className="text-slate-500">1+</span><span className="font-mono-num font-medium">{formatINR(current.rate)}</span></div>
                        {current.tier_prices.map((t) => (
                          <div key={t.min_qty} className="flex justify-between">
                            <span className="text-slate-500">{t.min_qty}+</span>
                            <span className="font-mono-num font-medium text-emerald-600">{formatINR(t.rate)}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  <div className="mt-5 flex gap-3">
                    <Button onClick={handleAdd} disabled={!current.in_stock} className="flex-1 rounded-full bg-slate-900 hover:bg-black text-white h-12" data-testid="detail-add-to-cart">
                      <ShoppingCart className="h-4 w-4 mr-1.5" /> Add {qty} to cart
                    </Button>
                    <QuoteRequestModal
                      prefillItems={[{ sku: product.sku, name: product.name }]}
                      trigger={<Button variant="outline" className="rounded-full h-12" data-testid="detail-quote-btn">Request quote</Button>}
                    />
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
}
